import { Field, RadioCard, Text } from '@noorden-insurtech/alba-design-system';
import { Briefcase, Users, Users2 } from 'lucide-react';
import type { TipoDePerfil } from '@/features/quotes/schemas/capital-global';
import {
  formatRiskCapitalInput,
  RISK_STEP_MAX_LIVES_VALUE,
  sanitizeLivesInput,
} from '../utils/step-helpers.util';

type Props = {
  tipoDePerfil: TipoDePerfil;
  onTipoDePerfilChange: (value: TipoDePerfil) => void;

  quantidadeDeVidas: string;
  onQuantidadeDeVidasChange: (value: string) => void;
  errorQuantidadeDeVidas?: string;

  capitalIndividual: string;
  onCapitalIndividualChange: (value: string) => void;
  errorCapitalIndividual?: string;
  /** Faixa permitida já formatada — quem resolve o teto do Tipo de Perfil é o step. */
  capitalIndividualHint: string;

  disabled?: boolean;
};

export function UniformeRiskFields({
  tipoDePerfil,
  onTipoDePerfilChange,
  quantidadeDeVidas,
  onQuantidadeDeVidasChange,
  errorQuantidadeDeVidas,
  capitalIndividual,
  onCapitalIndividualChange,
  errorCapitalIndividual,
  capitalIndividualHint,
  disabled,
}: Props) {
  return (
    <div className='flex flex-col gap-6'>
      {/* Tipo de Perfil */}
      <div className='flex flex-col gap-3'>
        <Text className='text-sm font-bold text-alba-black-500'>
          Tipo de Perfil <span className='text-alba-error-red-500'>*</span>
        </Text>

        <RadioCard.Root
          value={tipoDePerfil}
          onValueChange={details =>
            onTipoDePerfilChange(details.value as TipoDePerfil)
          }
          disabled={disabled}
        >
          <div className='flex gap-2'>
            <RadioCard.Item value='COLABORADORES' className='w-44'>
              <RadioCard.ItemControl className='flex-col items-start gap-3 h-full'>
                <div className='flex items-center justify-between w-full'>
                  <Users className='w-6 h-6' strokeWidth={1.6} />
                  <RadioCard.ItemIndicator />
                </div>
                <RadioCard.ItemText>Colaboradores</RadioCard.ItemText>
              </RadioCard.ItemControl>
              <RadioCard.ItemHiddenInput />
            </RadioCard.Item>

            <RadioCard.Item value='SOCIOS' className='w-44'>
              <RadioCard.ItemControl className='flex-col items-start gap-3 h-full'>
                <div className='flex items-center justify-between w-full'>
                  <Briefcase className='w-6 h-6' strokeWidth={1.6} />
                  <RadioCard.ItemIndicator />
                </div>
                <RadioCard.ItemText>Sócios</RadioCard.ItemText>
              </RadioCard.ItemControl>
              <RadioCard.ItemHiddenInput />
            </RadioCard.Item>

            <RadioCard.Item value='COLABORADORES_E_SOCIOS' className='w-44'>
              <RadioCard.ItemControl className='flex-col items-start gap-3 h-full'>
                <div className='flex items-center justify-between w-full'>
                  <Users2 className='w-6 h-6' strokeWidth={1.6} />
                  <RadioCard.ItemIndicator />
                </div>
                <RadioCard.ItemText>Colaboradores + Sócios</RadioCard.ItemText>
              </RadioCard.ItemControl>
              <RadioCard.ItemHiddenInput />
            </RadioCard.Item>
          </div>
        </RadioCard.Root>
      </div>

      {/* Campos de quantidade e capital */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
        <Field.Root invalid={!!errorQuantidadeDeVidas}>
          <Field.Label className='text-sm font-bold text-alba-black-500'>
            Quantidade de Vidas{' '}
            <span className='text-alba-error-red-500'>*</span>
          </Field.Label>
          <Field.Input
            type='number'
            min={1}
            max={RISK_STEP_MAX_LIVES_VALUE}
            value={quantidadeDeVidas}
            onChange={e =>
              onQuantidadeDeVidasChange(sanitizeLivesInput(e.target.value))
            }
            placeholder='Digite a quantidade'
            disabled={disabled}
          />
          {errorQuantidadeDeVidas && (
            <Field.ErrorText>{errorQuantidadeDeVidas}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={!!errorCapitalIndividual}>
          <Field.Label className='text-sm font-bold text-alba-black-500'>
            Capital Individual{' '}
            <span className='text-alba-error-red-500'>*</span>
          </Field.Label>
          <Field.Input
            type='text'
            inputMode='decimal'
            value={capitalIndividual}
            onChange={e =>
              onCapitalIndividualChange(formatRiskCapitalInput(e.target.value))
            }
            placeholder='R$ 0,00'
            disabled={disabled}
          />
          {errorCapitalIndividual ? (
            <Field.ErrorText>{errorCapitalIndividual}</Field.ErrorText>
          ) : (
            <Field.HelperText>{capitalIndividualHint}</Field.HelperText>
          )}
        </Field.Root>
      </div>
    </div>
  );
}
